import { useState, type ReactElement } from 'react';
import { Modal, type ModalAction } from './Modal';
import { basename } from '../fs/containment';
import type {
  PasteConflictChoice,
  PasteConflictPrompt,
} from '../fs/fsActions';

export interface PasteConflictDialogProps {
  prompt: PasteConflictPrompt;
  onResolve: (choice: PasteConflictChoice) => void;
}

/**
 * Modal shown when a paste would overwrite an existing file or folder in the
 * target directory. Offers three forward paths plus a cancel:
 *
 *   - **Replace** — overwrite the existing entry.
 *   - **Keep both** — paste under the next available `name copy.md` slot.
 *   - **Skip** — leave the existing entry alone and continue the batch.
 *   - **Cancel** — abort the rest of the paste batch entirely.
 *
 * When more conflicts may follow, an "Apply to all remaining" checkbox lets
 * the user pick once and breeze through the rest. The checkbox is hidden on
 * the final conflict.
 */
export function PasteConflictDialog({
  prompt,
  onResolve,
}: PasteConflictDialogProps): ReactElement {
  const [applyToAll, setApplyToAll] = useState(false);
  const name = basename(prompt.targetPath);

  const resolve = (action: PasteConflictChoice['action']): void => {
    onResolve({ action, applyToAll });
  };

  // Replace overwrites an existing entry (destructive), so it is NOT the Enter
  // default; Cancel is the safe Esc/Enter target.
  const actions: ModalAction[] = [
    { id: 'cancel', label: 'Cancel', onSelect: () => resolve('cancel'), accelerator: 'c', isCancel: true, isDefault: true },
    { id: 'skip', label: 'Skip', onSelect: () => resolve('skip'), accelerator: 's' },
    { id: 'keep-both', label: 'Keep both', onSelect: () => resolve('keep-both'), accelerator: 'k' },
    { id: 'replace', label: 'Replace', onSelect: () => resolve('replace'), accelerator: 'r', variant: 'danger' },
  ];

  return (
    <Modal
      open
      onClose={() => onResolve({ action: 'cancel' })}
      title="Item already exists"
      closeOnBackdrop={false}
      actions={actions}
    >
      <p>
        An item named <strong>{name}</strong> already exists at this location.
      </p>
      <p className="mp-modal__hint">{prompt.targetPath}</p>
      {prompt.hasMore && (
        <label className="mp-modal__checkbox">
          <input
            type="checkbox"
            checked={applyToAll}
            onChange={(e) => setApplyToAll(e.target.checked)}
          />
          Apply to all remaining conflicts
        </label>
      )}
    </Modal>
  );
}
